import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AppContainer from "../Structure/AppContainer";
import PermissionContext from "../../Context/PermissionContext";
import AuthContext from "../../Context/AuthContext";
import ajaxRolePermissionDistribution from "../../util/remote/ajaxRolePermissionDistribution";

const RolePermissionsDistribution = () => {
  const { userId } = useContext(AuthContext);
  const { permissionList } = useContext(PermissionContext) || {};
  const [RolePermissions, setRolePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [removedPermissions, setRemovedPermissions] = useState([]);
  const { role_id } = useParams();

  useEffect(() => {
    fetchRolePermissions();
    setSelectedPermissions([]);
    setRemovedPermissions([]);
  }, [role_id]);

  const fetchRolePermissions = async () => {
    if (!role_id) return;
    try {
      const response =
        await ajaxRolePermissionDistribution.fetchRolePermissionsDetailed({
          role_id,
        });
      if (response.status === "OK") {
        setRolePermissions(response.details || []);
      } else {
        toast.error("Failed to load allocated permissions");
      }
    } catch (err) {
      toast.error("Server error while fetching permissions");
    }
  };

  const handleCheck = (permissionId, alreadyAllocated) => {
    if (alreadyAllocated) {
      // Toggle removal for already allocated permissions
      setRemovedPermissions((prev) =>
        prev.includes(permissionId)
          ? prev.filter((id) => id !== permissionId)
          : [...prev, permissionId]
      );
    } else {
      // Toggle selection for new permissions
      setSelectedPermissions((prev) =>
        prev.includes(permissionId)
          ? prev.filter((id) => id !== permissionId)
          : [...prev, permissionId]
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPermissions.length && !removedPermissions.length) {
      toast.error("No changes made");
      return;
    }

    let data = {
      role_id,
      addPermissionIDs: selectedPermissions,
      removePermissionIDs: removedPermissions,
    };

    const response =
      await ajaxRolePermissionDistribution.createRolePermissionDistribution(
        data
      );

    if (response.status === "OK") {
      toast.success(response.message);
      setSelectedPermissions([]);
      setRemovedPermissions([]);
      fetchRolePermissions();
    } else {
      toast.error(response.message);
    }
  };

  return (
    <AppContainer title="Permissions Distribution">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm p-3">
              <div className="table-responsive">
                <table className="table table-striped align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th style={{ color: "white" }}>#</th>
                      <th style={{ color: "white" }}>Permission Code</th>
                      <th style={{ color: "white" }}>Description</th>
                      <th style={{ color: "white" }} className="text-center">
                        Allocate / Remove
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {permissionList?.length ? (
                      permissionList.map((perm, index) => {
                        const alreadyAllocated = RolePermissions.some(
                          (p) =>
                            parseInt(p.permission_id) ===
                            parseInt(perm.permission_id)
                        );

                        const checked =
                          (!alreadyAllocated &&
                            selectedPermissions.includes(perm.permission_id)) ||
                          (alreadyAllocated &&
                            !removedPermissions.includes(perm.permission_id));

                        const rowClass = removedPermissions.includes(
                          perm.permission_id
                        )
                          ? "text-danger" // red for removed
                          : alreadyAllocated
                          ? "text-success" // green for allocated
                          : ""; // default for new/unselected

                        return (
                          <tr key={perm.permission_id} className={rowClass}>
                            <td>{index + 1}</td>
                            <td>{perm.permission_code}</td>
                            <td>{perm.permission_description}</td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  handleCheck(
                                    perm.permission_id,
                                    alreadyAllocated
                                  )
                                }
                              />
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-3">
                          No permissions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="text-end mt-3">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleSubmit}
                  disabled={
                    !selectedPermissions.length && !removedPermissions.length
                  }
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppContainer>
  );
};

export default RolePermissionsDistribution;
